import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { Filters } from './filters/filters';
import { OfferTableComponent } from './offer-table-component/offer-table-component';
import { OffersService } from '../../../../../services/offers/offers.service';
import { Pagination } from '../../../../../shared/ui/shared-tables/pagination/pagination';
import {
  buildPageNumbers,
  getEndItem,
  getStartItem,
  LoadState,
} from '../../../../../shared/ui/shared-tables/pagination/pagination.utils';
import { ConfirmationDialog } from '../../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../../utils/successful-notification/successful-notification';
import {
  OfferFiltersApi,
  OfferListItem,
} from '../../../../../pages/admin-dashboard/promotions/offers-management/offers.model';

@Component({
  selector: 'app-offers-list-component',
  imports: [Filters, OfferTableComponent, Pagination, ConfirmationDialog, SuccessfulNotification],
  templateUrl: './offers-list-component.html',
  styleUrl: './offers-list-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OffersListComponent implements OnInit {
  private locale = inject(LOCALE_ID);
  private offersService = inject(OffersService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  isArabic = computed(() => this.locale.startsWith('ar'));

  offersData = signal<OfferListItem[]>([]);
  loadingOffers = signal(true);
  errorOffers = signal('');
  state = computed<LoadState>(() => (this.loadingOffers() ? 'loading' : this.errorOffers() ? 'error' : 'success'));
  isFilterApplied = signal(false);

  filters = signal<OfferFiltersApi>({
    title: '',
    offerTypeIds: undefined,
    status: undefined,
    orderBy: 'TITLE',
    orderDir: 'DESC',
    startDateFrom: '',
    startDateTo: '',
    endDateFrom: '',
    endDateTo: '',
  });

  totalItems = signal(0);
  pageSize = signal(25);
  currentPage = signal(1);
  totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.pageSize())));
  startItem = computed(() => getStartItem(this.currentPage(), this.pageSize(), this.totalItems()));
  endItem = computed(() => getEndItem(this.currentPage(), this.pageSize(), this.totalItems()));
  pageNumbers = computed(() => buildPageNumbers(this.currentPage(), this.totalPages()));

  deleteConfirmationModal = signal(false);
  offerIdToDelete = signal<string | null>(null);
  loadingDelete = signal(false);
  errorDelete = signal('');
  deleteSuccess = signal(false);

  confirmationModal = signal(false);
  type = signal<'deactivate' | 'activate' | null>(null);
  offerIdToActivate = signal<string | null>(null);
  errorActivation = signal('');
  isSuccessNotification = signal(false);
  loadingActivation = signal(false);

  ngOnInit() {
    this.loadOffers();
  }

  loadOffers() {
    this.loadingOffers.set(true);
    this.errorOffers.set('');
    const filters = this.filters();

    const subscription = this.offersService
      .getAllOffersByFilter({
        ...filters,
        pageNum: this.currentPage() - 1,
        pageSize: this.pageSize(),
      })
      .pipe(finalize(() => this.loadingOffers.set(false)))
      .subscribe({
        next: (res) => {
          this.offersData.set(res.list);
          this.totalItems.set(res.total);
        },
        error: (err) => {
          this.errorOffers.set(
            this.isArabic() ? err.messageAr || 'حدث بعض الخطإ!' : err.messageEn || 'Some error occured!',
          );
        },
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onApplyFilters(filters: OfferFiltersApi) {
    this.isFilterApplied.set(true);
    this.filters.set({ ...filters });
    this.currentPage.set(1);
    this.loadOffers();
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadOffers();
    }
  }

  onPageSizeChange(size: number) {
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.loadOffers();
  }

  onNavigateToOffer(offerId: string) {
    this.router.navigate(['/admin/offers/offer-details/', offerId]);
  }

  onNavigateToEditOffer(offerId: string) {
    this.router.navigate(['/admin/offers/edit-offer/', offerId]);
  }

  onDeleteOffer(offerId: string) {
    this.offerIdToDelete.set(offerId);
    this.errorDelete.set('');
    this.deleteConfirmationModal.set(true);
  }

  onActiveConfirmationModal({ id, isActive }: { id: string; isActive: boolean }) {
    this.confirmationModal.set(true);
    this.type.set(isActive ? 'deactivate' : 'activate');
    this.offerIdToActivate.set(id);
  }

  deactivateOffer() {}
  activateOffer() {}
  confirmDelete() {}

  confirmationModalContent = computed(() => {
    const ar = this.isArabic();
    const type = this.type();
    const loading = this.loadingActivation();

    const content = {
      deactivate: {
        title: ar ? 'تأكيد إلغاء التفعيل' : 'Confirm Deactivation',
        description: ar
          ? 'هل أنت متأكد من رغبتك في إلغاء تفعيل هذا العرض'
          : 'Are you sure you want to deactivate this offer?',
        confirmContent: ar
          ? loading
            ? 'جارٍ إلغاء التفعيل...'
            : 'تأكيد إلغاء التفعيل'
          : loading
            ? 'Deactivating...'
            : 'Confirm Deactivation',
      },
      activate: {
        title: ar ? 'تأكيد تفعيل العرض' : 'Confirm Offer Activation',
        description: ar
          ? 'هل أنت متأكد من رغبتك في تفعيل هذا العرض؟ '
          : 'Are you sure you want to activate this offer? ',
        confirmContent: ar
          ? loading
            ? 'جارٍ التفعيل...'
            : 'تأكيد التفعيل'
          : loading
            ? 'Activating...'
            : 'Confirm Activation',
      },
    };

    return content[type!];
  });
}
